import { useEffect, useRef, useState } from "react";
import { setAppBit } from "../api/sacada";
import useWebSocket from "../hooks/useWebSockets";
import { useAuth } from "../context/AuthContext";

export default function Surveillance({ inactivePreview = false }) {
  const { token } = useAuth();
  const {
    data,
    lastMessage,
    connected,
    stale,
    error,
  } = useWebSocket("surveillance", !inactivePreview);

  const tempOn = data?.capteur_temperature ?? false;
  const humOn  = data?.capteur_humidite ?? false;
  const pirOn  = data?.capteur_mouvement ?? false;
  const buzzer = data?.alarme_buzzer ?? false;
  const ledRouge = data?.led_rouge ?? false;
  const boutonStopStatut = data?.bouton_stop ?? false;
  const tempVal = data?.temperature_c;
  const humVal = data?.humidity_percent;
  const detectionCount = data?.detections_total;
  const commandsEnabled = lastMessage?.commands_enabled === true;
  const [sendingBit, setSendingBit] = useState(null);
  const [, setLastCommandLatencyMs] = useState(null);
  const pendingCommandTraces = useRef(new Map());
  const controlsDisabled = !connected || stale || data === null || !commandsEnabled;

  const sendCommand = async (bit, state) => {
    if (controlsDisabled || sendingBit !== null) return;
    const correlationId = window.crypto?.randomUUID?.()
      || `surveillance-${Date.now()}-${Math.round(performance.now() * 1000)}`;
    pendingCommandTraces.current.set(correlationId, performance.now());
    setSendingBit(bit);
    try {
      await setAppBit("surveillance", bit, state, token, correlationId);
    } catch (commandError) {
      pendingCommandTraces.current.delete(correlationId);
      console.error("Commande Surveillance refusée :", commandError);
    } finally {
      setSendingBit(null);
    }
  };

  useEffect(() => {
    const traces = lastMessage?.command_traces || [];
    traces.forEach((trace) => {
      const startedAt = pendingCommandTraces.current.get(trace.correlation_id);
      if (startedAt === undefined) return;
      pendingCommandTraces.current.delete(trace.correlation_id);
      requestAnimationFrame(() => {
        setLastCommandLatencyMs(Math.round((performance.now() - startedAt) * 10) / 10);
      });
    });
  }, [lastMessage]);

  const toggleTemp = () => sendCommand(1, !tempOn);
  const toggleHum = () => sendCommand(2, !humOn);
  const stopBuzzer = () => sendCommand(3, !boutonStopStatut);

  const arcOffset = (pct) => 196.35 - (196.35 * Math.min(pct, 1));
  const tPct = tempVal == null ? 0 : (tempVal - 18) / (45 - 18);
  const hPct = humVal == null ? 0 : (humVal - 30) / (90 - 30);

  return (
    <div style={{ fontFamily: "'Rajdhani', sans-serif", background: "#f0f4f5", minHeight: "100vh", padding: "18px", color: "#555454" }}>

      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", borderRadius: "12px", padding: "14px 22px", marginBottom: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderLeft: "4px solid #057c82" }}>
        <div>
          <div style={{ fontSize: "20px", fontWeight: 700, color: "#057c82", letterSpacing: "3px", textTransform: "uppercase", textAlign: "center", width: "100%" }}>🛡️ SURVEILLANCE ENVIRONNEMENTALE</div>
          <div style={{ fontSize: "11px", fontFamily: "monospace", color: "#aaa", marginTop: "2px" }}>MEA PULSE SCADA · Supervision environnementale</div>
        </div>
        <div style={{
          display: "flex", alignItems: "center", gap: "8px", padding: "6px 16px",
          borderRadius: "20px", fontSize: "12px", fontWeight: 600, border: "1.5px solid",
          background: buzzer ? "#fff3f3" : pirOn ? "#fff8f0" : "#f0faf5",
          color: buzzer ? "#c0392b" : pirOn ? "#d97706" : "#057c82",
          borderColor: buzzer ? "#f5b5b5" : pirOn ? "#fcd98b" : "#b2ddd6"
        }}>
          <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "currentColor" }} />
          {buzzer ? "⚠️ ALARME ACTIVE" : pirOn ? "👁️ Mouvement détecté" : "Système nominal"}
        </div>
      </div>

      {(!connected || stale || error) && (
        <div role="alert" style={{
          display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px",
          padding: "10px 14px", borderRadius: "10px", marginBottom: "14px",
          background: "#fff4e0", border: "1px solid #ffd9a0", color: "#8a5200",
          fontSize: "12px", fontWeight: 700,
        }}>
          <span>
            {!connected ? "BACKEND DÉCONNECTÉ — dernière valeur connue conservée"
              : stale ? "DONNÉES OBSOLÈTES — commandes désactivées"
              : "ERREUR DE DONNÉES"}
          </span>
          {error && <span>· {error}</span>}
        </div>
      )}

      {/* GRID */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>

        {/* ── TEMP + HUM (pleine largeur) ── */}
        <div style={{ gridColumn: "1/-1", background: "#fff", borderRadius: "14px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", fontSize: "14px", fontWeight: 700 }}>
            <div style={{ width: "30px", height: "30px", borderRadius: "8px", background: "#fff3ee", display: "flex", alignItems: "center", justifyContent: "center" }}>🌡️</div>
            Capteurs Température & Humidité
            <span style={{ fontSize: "10px", fontFamily: "monospace", padding: "2px 8px", borderRadius: "12px", fontWeight: 600, background: buzzer ? "#ffeaea" : "#e8f5f0", color: buzzer ? "#c0392b" : "#057c82" }}>
              {buzzer ? "ALARME" : "NOMINAL"}
            </span>
          </div>

          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "flex-start" }}>

            {/* GAUGE TEMP */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
              <svg width="130" height="130" viewBox="0 0 130 130">
                <circle cx="65" cy="65" r="52" fill="none" stroke="#eef2f3" strokeWidth="10" strokeDasharray="261.8" strokeDashoffset="65.45" strokeLinecap="round" transform="rotate(135 65 65)" />
                <circle cx="65" cy="65" r="52" fill="none" stroke={tempOn ? "#c0392b" : "#057c82"} strokeWidth="10" strokeLinecap="round"
                  strokeDasharray="261.8" strokeDashoffset={arcOffset(tPct)}
                  transform="rotate(135 65 65)" style={{ transition: "stroke-dashoffset 1.2s ease, stroke 0.4s" }} />
                <text x="65" y="60" textAnchor="middle" style={{ fontFamily: "monospace", fontSize: "18px", fill: "#555454", fontWeight: 700 }}>{tempVal == null ? "—" : `${tempVal.toFixed(1)}°C`}</text>
                <text x="65" y="76" textAnchor="middle" style={{ fontFamily: "Rajdhani", fontSize: "11px", fill: "#aaa", fontWeight: 600, letterSpacing: "1px" }}>TEMP</text>
              </svg>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "#888" }}>Température</div>
            </div>

            {/* GAUGE HUM */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
              <svg width="130" height="130" viewBox="0 0 130 130">
                <circle cx="65" cy="65" r="52" fill="none" stroke="#eef2f3" strokeWidth="10" strokeDasharray="261.8" strokeDashoffset="65.45" strokeLinecap="round" transform="rotate(135 65 65)" />
                <circle cx="65" cy="65" r="52" fill="none" stroke={humOn ? "#1a6ad4" : "#057c82"} strokeWidth="10" strokeLinecap="round"
                  strokeDasharray="261.8" strokeDashoffset={arcOffset(hPct)}
                  transform="rotate(135 65 65)" style={{ transition: "stroke-dashoffset 1.2s ease, stroke 0.4s" }} />
                <text x="65" y="60" textAnchor="middle" style={{ fontFamily: "monospace", fontSize: "18px", fill: "#555454", fontWeight: 700 }}>{humVal == null ? "—" : `${humVal.toFixed(0)}%`}</text>
                <text x="65" y="76" textAnchor="middle" style={{ fontFamily: "Rajdhani", fontSize: "11px", fill: "#aaa", fontWeight: 600, letterSpacing: "1px" }}>HUM</text>
              </svg>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "#888" }}>Humidité</div>
            </div>

            {/* CORR PANEL */}
            <div style={{ flex: 1, minWidth: "180px", background: "#f7fafa", borderRadius: "10px", padding: "14px", border: "1px solid #e0ecee" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#057c82", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "12px" }}>Corrélation Temp / Humidité</div>

              {[{ label: "Température", pct: tPct, val: tempVal == null ? "—" : tempVal.toFixed(1) + "°C", color: "#e05c2a" },
                { label: "Humidité", pct: hPct, val: humVal == null ? "—" : humVal.toFixed(0) + "%", color: "#1a6ad4" }].map(({ label, pct, val, color }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                  <span style={{ fontSize: "12px", color: "#777", minWidth: "90px", fontWeight: 600 }}>{label}</span>
                  <div style={{ flex: 1, height: "7px", background: "#e0ecee", borderRadius: "4px", overflow: "hidden" }}>
                    <div style={{ height: "100%", borderRadius: "4px", width: (pct * 100) + "%", background: `linear-gradient(90deg,#057c82,${color})`, transition: "width 1.2s ease" }} />
                  </div>
                  <span style={{ fontFamily: "monospace", fontSize: "12px", fontWeight: 700, minWidth: "42px", textAlign: "right", color }}>{val}</span>
                </div>
              ))}

              {/* BUZZER */}
              <div style={{
                display: "flex", alignItems: "center", gap: "12px", padding: "12px 14px",
                borderRadius: "10px", border: `1.5px solid ${buzzer ? "#e03c3c" : "#e0ecee"}`,
                background: buzzer ? "#fff3f3" : "#f7fafa", marginTop: "12px",
                transition: "all 0.4s"
              }}>
                <div style={{ fontSize: "28px" }}>{buzzer ? "🔊" : "🔇"}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "13px", fontWeight: 700 }}>BUZZER — Sortie O1</div>
                  <div style={{ fontSize: "11px", fontFamily: "monospace", marginTop: "2px", color: buzzer ? "#c0392b" : "#aaa" }}>
                    {buzzer ? "ACTIF — Seuil dépassé" : "INACTIF"}
                  </div>
                </div>
                <span style={{ fontSize: "10px", fontFamily: "monospace", padding: "2px 8px", borderRadius: "12px", fontWeight: 600, background: buzzer ? "#ffeaea" : "#e8f5f0", color: buzzer ? "#c0392b" : "#057c82" }}>
                  {buzzer ? "ON" : "OFF"}
                </span>
              </div>

              {/* DÉTECTIONS — un seul bloc, total + par jour, sans couleur distinctive */}
              <div style={{
                padding: "12px 14px",
                borderRadius: "10px", border: "1.5px solid #e0ecee",
                background: "#f7fafa", marginTop: "10px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 700, marginBottom: "10px" }}>
                  <span style={{ fontSize: "18px" }}>📊</span> Détections cumulées
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <div style={{ flex: 1, textAlign: "center", padding: "8px", borderRadius: "8px", border: "1px solid #e0ecee" }}>
                    <div style={{ fontSize: "10px", fontFamily: "monospace", color: "#aaa" }}>total</div>
                    <div style={{ fontSize: "20px", fontFamily: "monospace", fontWeight: 800, color: "#555454" }}>{detectionCount ?? "—"}</div>
                  </div>
                  <div style={{ flex: 1, textAlign: "center", padding: "8px", borderRadius: "8px", border: "1px solid #e0ecee" }}>
                    <div style={{ fontSize: "10px", fontFamily: "monospace", color: "#aaa" }}>temps backend</div>
                    <div style={{ fontSize: "20px", fontFamily: "monospace", fontWeight: 800, color: "#555454" }}>{data?.operating_time_seconds == null ? "—" : `${data.operating_time_seconds}s`}</div>
                  </div>
                </div>
              </div>

              {/* BOUTONS TEMP + HUM */}
              <div style={{ marginTop: "14px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#057c82", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "10px" }}>Simuler capteurs</div>
                <div style={{ display: "flex", gap: "10px" }}>
                  {[
                    { label: "Capteur de Température", icon: "🌡️", state: tempOn, toggle: toggleTemp, onColor: "#e05c2a", onBg: "#fff3ee" },
                    { label: "Capteur de Humidité", icon: "💧", state: humOn, toggle: toggleHum, onColor: "#1a6ad4", onBg: "#eef4ff" }
                  ].map(({ label, icon, state, toggle, onColor, onBg }) => (
                    <button key={label} onClick={toggle} disabled={controlsDisabled || boutonStopStatut || sendingBit !== null} style={{
                      flex: 1, padding: "14px", borderRadius: "10px", cursor: controlsDisabled || boutonStopStatut || sendingBit !== null ? "not-allowed" : "pointer",
                      border: `2px solid ${state ? onColor : "#e0ecee"}`,
                      background: state ? onBg : "#f7fafa",
                      color: state ? onColor : "#555454",
                      fontFamily: "Rajdhani", fontSize: "13px", fontWeight: 700,
                      display: "flex", flexDirection: "column", alignItems: "center", gap: "6px",
                      transition: "all 0.25s", boxShadow: state ? `0 0 14px ${onColor}33` : "none"
                    }}>
                      <span style={{ fontSize: "24px" }}>{icon}</span>
                      {label}
                      <span style={{ fontSize: "10px", fontFamily: "monospace", color: state ? onColor : "#aaa" }}>
                        
                      </span>
                    </button>
                  ))}
                </div>

                {/* ── BOUTON STOP — sous les 2 boutons de simulation, dans la même carte ── */}
                <button onClick={stopBuzzer} disabled={controlsDisabled || sendingBit !== null} style={{
                  width: "100%", marginTop: "10px", padding: "14px", borderRadius: "10px",
                  cursor: (controlsDisabled || sendingBit !== null) ? "not-allowed" : "pointer", border: "none",
                  background: boutonStopStatut ? "#8e1f16" : sendingBit === 3 ? "#e8a5a5" : "#c0392b", color: "#fff",
                  fontFamily: "Rajdhani", fontSize: "14px", fontWeight: 700, letterSpacing: "1px",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  boxShadow: boutonStopStatut ? "0 0 0 2px #c0392b55" : "none", transition: "all 0.2s"
                }}>
                  <span style={{ width: "12px", height: "12px", borderRadius: "3px", background: "#fff" }} />
                  {sendingBit === 3 ? "…" : boutonStopStatut ? "RÉARMER STOP" : "STOP"}
                </button>
              </div>
            </div>
          </div>

          {/* ALERT BAR */}
          <div style={{
            display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px",
            borderRadius: "8px", fontSize: "13px", fontWeight: 600, marginTop: "14px",
            background: buzzer ? "#fff3f3" : "#f0faf5",
            color: buzzer ? "#c0392b" : "#057c82",
            border: `1px solid ${buzzer ? "#f5b5b5" : "#b2ddd6"}`
          }}>
            <span>{buzzer ? "⚠️ Seuil dépassé — Capteur actif → Buzzer déclenché" : "✅ Température et humidité dans les plages normales"}</span>
          </div>
        </div>

        {/* ── IO TABLE (occupe maintenant toute la largeur suite au retrait du panneau PIR) ── */}
        <div style={{ gridColumn: "1/-1", background: "#fff", borderRadius: "14px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", fontSize: "14px", fontWeight: 700 }}>
            <div style={{ width: "30px", height: "30px", borderRadius: "8px", background: "#eef4ff", display: "flex", alignItems: "center", justifyContent: "center" }}>⚡</div>
            État des Entrées / Sorties PLC
          </div>

          {[
            { section: "ENTRÉES / COMMANDES (25)", items: [
              { id: "i1", label: "Bit 0 — Capteur Mouvement", addr: "25.0", active: pirOn, color: "#057c82", cls: "motion" },
              { id: "i2", label: "Bit 1 — Capteur Température", addr: "25.1", active: tempOn, color: "#c0392b", cls: "alarm" },
              { id: "i3", label: "Bit 2 — Capteur Humidité", addr: "25.2", active: humOn, color: "#1a6ad4", cls: "alarm" },
              { id: "cmd_stop", label: "Bit 3 — Bouton Stop (commande)", addr: "25.3", active: boutonStopStatut, color: "#c0392b", cls: "alarm", button: true }
            ]},
            { section: "SORTIES / STATUT (50)", items: [
              { id: "o1", label: "Bit 3 — Alarme / Buzzer", addr: "50.3", active: buzzer, color: "#c0392b", cls: "alarm" },
              { id: "o2", label: "Bit 4 — LED (led_rouge)", addr: "50.4", active: ledRouge, color: "#c0392b", cls: "alarm" },
              { id: "stat_stop", label: "Bit 5 — Bouton Stop (statut)", addr: "50.5", active: boutonStopStatut, color: "#057c82", cls: "ok" }
            ]}
          ].map(({ section, items }) => (
            <div key={section}>
              <div style={{ fontSize: "10px", color: "#057c82", fontFamily: "monospace", fontWeight: 700, letterSpacing: "1px", marginBottom: "6px", marginTop: section.startsWith("SORTIES") ? "12px" : "0" }}>{section}</div>
              {items.map(({ id, label, addr, active, color, cls, button }) => (
                <div key={id} style={{
                  display: "flex", alignItems: "center", gap: "10px", padding: "9px 12px",
                  borderRadius: "8px", marginBottom: "6px", transition: "all 0.3s",
                  background: active ? (cls === "alarm" ? "#fff3f3" : cls === "motion" ? "#fff8f0" : "#f0faf5") : "#f7fafa",
                  border: `1px solid ${active ? (cls === "alarm" ? "#f5b5b5" : cls === "motion" ? "#fcd98b" : "#b2ddd6") : "#e8eef0"}`
                }}>
                  <div style={{ width: "9px", height: "9px", borderRadius: "50%", background: active ? color : "#ddd", boxShadow: active ? `0 0 6px ${color}` : "none", transition: "all 0.3s", flexShrink: 0 }} />
                  <span style={{ fontSize: "12px", fontWeight: 600, flex: 1 }}>{label}</span>
                  <span style={{ fontFamily: "monospace", fontSize: "10px", color: "#aaa" }}>{addr}</span>
                  <span style={{ fontFamily: "monospace", fontSize: "12px", fontWeight: 700, color: active ? color : "#aaa" }}>{active ? "1" : "0"}</span>
                  {/* Bouton stop réel — aussi accessible directement depuis le tableau E/S */}
                  {button && (
                    <button onClick={stopBuzzer} disabled={controlsDisabled || sendingBit !== null} style={{
                      padding: "5px 10px", borderRadius: "6px", cursor: (controlsDisabled || sendingBit !== null) ? "not-allowed" : "pointer",
                      border: "1.5px solid #c0392b", background: boutonStopStatut ? "#8e1f16" : sendingBit === 3 ? "#e8a5a5" : "#c0392b", color: "#fff",
                      fontFamily: "Rajdhani", fontSize: "11px", fontWeight: 700
                    }}>
                      {sendingBit === 3 ? "…" : boutonStopStatut ? "⏹ Réarmer" : "⏹ Stop"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        
      </div>

      {/* CSS ANIMATION pour les rings PIR (conservée pour le bloc commenté ci-dessus) */}
      <style>{`
        @keyframes ring-expand {
          0% { r: 12; opacity: 0.8; stroke-width: 3; }
          100% { r: 40; opacity: 0; stroke-width: 0.5; }
        }
      `}</style>
    </div>
  );
}
